import { useEffect, useMemo, useState } from 'react'
import {
  CalendarClock,
  ExternalLink,
  Mail,
  Phone,
  ReceiptText,
  Save,
  Star,
  Trash2,
  X,
} from 'lucide-react'
import { calculatePriority } from '../lib/priority'
import type { Client, ClientStatus, Interaction, Invoice } from '../types'
import { CLIENT_STATUS_OPTIONS } from '../types'
import { invoiceTiming, todayInMoscow } from '../workspace/billing'

interface ClientPanelProps {
  client: Client
  onClose: () => void
  onDelete: (client: Client) => void
  onSave: (client: Client) => void
  invoices?: Invoice[]
  onSaveInvoice?: (invoice: Invoice) => void
}

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value)
}

function latestDateOnly(...values: Array<string | null>): string {
  return values.filter((value): value is string => Boolean(value)).sort().at(-1) ?? todayInMoscow()
}

export function ClientPanel({
  client,
  onClose,
  onDelete,
  onSave,
  invoices = [],
  onSaveInvoice,
}: ClientPanelProps) {
  const [draft, setDraft] = useState(client)
  const [contactStatus, setContactStatus] = useState<ClientStatus>(client.status)
  const [outcome, setOutcome] = useState(client.result)
  const [nextContact, setNextContact] = useState(toLocalInput(client.nextContactAt))

  useEffect(() => {
    setDraft(client)
    setContactStatus(client.status)
    setOutcome(client.result)
    setNextContact(toLocalInput(client.nextContactAt))
  }, [client])

  const priority = useMemo(() => calculatePriority(draft), [draft])

  function update<K extends keyof Client>(field: K, value: Client[K]) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function saveCard() {
    onSave({ ...draft, updatedAt: new Date().toISOString() })
  }

  function logContact() {
    const timestamp = new Date().toISOString()
    const nextContactAt = fromLocalInput(nextContact)
    const interaction: Interaction = {
      id: crypto.randomUUID(),
      createdAt: timestamp,
      status: contactStatus,
      outcome: outcome.trim(),
      nextContactAt,
    }
    const updated: Client = {
      ...draft,
      status: contactStatus,
      result: outcome.trim(),
      nextContactAt,
      attempts: draft.attempts + 1,
      lastContactAt: timestamp,
      interactions: [interaction, ...draft.interactions],
      updatedAt: timestamp,
    }
    setDraft(updated)
    onSave(updated)
  }

  return (
    <aside className="client-panel" aria-label={`Карточка ${client.company}`}>
      <div className="panel-header">
        <div>
          <span className="eyebrow">Карточка клиента</span>
          <h2>{draft.company || 'Новый клиент'}</h2>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть">
          <X size={20} />
        </button>
      </div>

      <div className={`priority-explain ${priority.level}`}>
        <div>
          <span>Приоритет</span>
          <strong>{priority.score} · {priority.label}</strong>
        </div>
        <ul>
          {priority.reasons.slice(0, 3).map((reason) => <li key={reason}>{reason}</li>)}
        </ul>
      </div>

      <div className="panel-scroll">
        {draft.osint ? (
          <section className="panel-section osint-section">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Каталогизация</span>
                <h3>OSINT-факты</h3>
              </div>
              <span>
                {draft.osint.collectedAt
                  ? `на ${new Intl.DateTimeFormat('ru-RU').format(new Date(draft.osint.collectedAt))}`
                  : 'из отчёта'}
              </span>
            </div>
            <div className="osint-badges">
              {draft.osint.rank ? <span>Рейтинг #{draft.osint.rank}</span> : null}
              {draft.osint.segment ? <span>{draft.osint.segment}</span> : null}
              {draft.osint.legalType ? <span>{draft.osint.legalType}</span> : null}
              {draft.osint.revenueCategory ? <span>{draft.osint.revenueCategory}</span> : null}
              {draft.osint.companyStatus ? <span>{draft.osint.companyStatus}</span> : null}
              <span className={draft.osint.governmentContracts === 'yes' ? 'positive' : ''}>
                {draft.osint.governmentContracts === 'yes'
                  ? `Госконтракты · от ${draft.osint.minimumContracts}`
                  : draft.osint.governmentContracts === 'not_found'
                    ? 'Госконтракты не найдены'
                    : 'Госконтракты: нет данных'}
              </span>
            </div>
            <dl className="osint-grid">
              <div>
                <dt>ИНН / КПП</dt>
                <dd>{draft.taxId || '—'}{draft.osint.kpp ? ` / ${draft.osint.kpp}` : ''}</dd>
              </div>
              <div>
                <dt>Выручка</dt>
                <dd>
                  {formatMoney(draft.osint.revenue)}
                  {draft.osint.revenueYear ? ` · ${draft.osint.revenueYear}` : ''}
                </dd>
              </div>
              <div className="span-2">
                <dt>Деятельность</dt>
                <dd>{draft.osint.activity || '—'}</dd>
              </div>
              {draft.osint.classDemand || draft.osint.profileClass ? (
                <div className="span-2">
                  <dt>Исходная классификация</dt>
                  <dd>
                    {[draft.osint.classDemand, draft.osint.profileClass]
                      .filter(Boolean)
                      .join(' · ')}
                  </dd>
                </div>
              ) : null}
            </dl>

            {draft.osint.latestContract ? (
              <article className="osint-card">
                <div>
                  <strong>Последний найденный контракт</strong>
                  <span>
                    {[
                      draft.osint.latestContract.laws,
                      draft.osint.latestContract.date,
                      formatMoney(draft.osint.latestContract.price),
                    ]
                      .filter((value) => value && value !== '—')
                      .join(' · ')}
                  </span>
                </div>
                {draft.osint.latestContract.customer ? (
                  <p><b>Заказчик:</b> {draft.osint.latestContract.customer}</p>
                ) : null}
                {draft.osint.latestContract.subject ? (
                  <p>{draft.osint.latestContract.subject}</p>
                ) : null}
                {draft.osint.latestContract.url ? (
                  <a
                    href={draft.osint.latestContract.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Карточка ЕИС <ExternalLink size={14} />
                  </a>
                ) : null}
              </article>
            ) : null}

            {draft.osint.tenderProfile ? (
              <article className="osint-card compact">
                <div>
                  <strong>B2B.House</strong>
                  <span>
                    {draft.osint.tenderProfile.winners} побед ·{' '}
                    {draft.osint.tenderProfile.completed} завершено ·{' '}
                    {draft.osint.tenderProfile.total} записей
                  </span>
                </div>
                {draft.osint.tenderProfile.organizationUrl ? (
                  <a
                    href={draft.osint.tenderProfile.organizationUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Профиль организации <ExternalLink size={14} />
                  </a>
                ) : null}
              </article>
            ) : null}

            {draft.osint.contact.address ||
            draft.osint.contact.phone ||
            draft.osint.contact.email ||
            draft.osint.contact.website ? (
              <article className="osint-card compact">
                <strong>Открытые контакты</strong>
                {draft.osint.contact.address ? <p>{draft.osint.contact.address}</p> : null}
                <div className="osint-links">
                  {draft.osint.contact.phone ? (
                    <a href={`tel:${draft.osint.contact.phone}`}>{draft.osint.contact.phone}</a>
                  ) : null}
                  {draft.osint.contact.email ? (
                    <a href={`mailto:${draft.osint.contact.email}`}>{draft.osint.contact.email}</a>
                  ) : null}
                  {draft.osint.contact.website ? (
                    <a href={draft.osint.contact.website} target="_blank" rel="noreferrer">
                      Сайт <ExternalLink size={13} />
                    </a>
                  ) : null}
                </div>
              </article>
            ) : null}

            {draft.osint.sources.length ? (
              <details className="osint-sources">
                <summary>Источники · {draft.osint.sources.length}</summary>
                <div>
                  {draft.osint.sources.map((source, index) => (
                    <a href={source} target="_blank" rel="noreferrer" key={source}>
                      Источник {index + 1} <ExternalLink size={12} />
                    </a>
                  ))}
                </div>
              </details>
            ) : null}
            <p className="osint-note">
              Эти сведения хранятся отдельно и не меняются при фиксации звонков.
            </p>
          </section>
        ) : null}

        <section className="panel-section">
          <div className="section-heading">
            <h3>Основные данные</h3>
            <button
              className={`star-button ${draft.important ? 'active' : ''}`}
              type="button"
              onClick={() => update('important', !draft.important)}
            >
              <Star size={17} fill={draft.important ? 'currentColor' : 'none'} />
              {draft.important ? 'В работе' : 'Добавить в работу'}
            </button>
          </div>
          <div className="form-grid">
            <label className="span-2">
              <span>Компания</span>
              <input value={draft.company} onChange={(event) => update('company', event.target.value)} />
            </label>
            <label>
              <span>ИНН</span>
              <input
                inputMode="numeric"
                value={draft.taxId ?? ''}
                onChange={(event) => update('taxId', event.target.value.replace(/\D/g, ''))}
              />
            </label>
            <label>
              <span>Контактное лицо</span>
              <input
                value={draft.contactPerson}
                onChange={(event) => update('contactPerson', event.target.value)}
              />
            </label>
            <label>
              <span>Город</span>
              <input value={draft.city} onChange={(event) => update('city', event.target.value)} />
            </label>
            <label>
              <span>Телефон</span>
              <div className="input-with-action">
                <input value={draft.phone} onChange={(event) => update('phone', event.target.value)} />
                {draft.phone ? <a href={`tel:${draft.phone}`} aria-label="Позвонить"><Phone size={17} /></a> : null}
              </div>
            </label>
            <label>
              <span>E-mail</span>
              <div className="input-with-action">
                <input value={draft.email} onChange={(event) => update('email', event.target.value)} />
                {draft.email ? <a href={`mailto:${draft.email}`} aria-label="Написать"><Mail size={17} /></a> : null}
              </div>
            </label>
            <label>
              <span>Сегмент</span>
              <input value={draft.segment} onChange={(event) => update('segment', event.target.value)} />
            </label>
            <label>
              <span>Потенциал, ₽</span>
              <input
                type="number"
                min="0"
                value={draft.potential ?? ''}
                onChange={(event) => update('potential', event.target.value ? Number(event.target.value) : null)}
              />
            </label>
            <label>
              <span>Отсрочка оплаты</span>
              <select
                value={[0, 30, 45, 60, 90].includes(draft.paymentTermsDays ?? 30)
                  ? String(draft.paymentTermsDays ?? 30)
                  : 'custom'}
                onChange={(event) => {
                  if (event.target.value !== 'custom') {
                    update('paymentTermsDays', Number(event.target.value))
                  } else if ([0, 30, 45, 60, 90].includes(draft.paymentTermsDays ?? 30)) {
                    update('paymentTermsDays', 1)
                  }
                }}
              >
                <option value="0">Предоплата</option>
                <option value="30">30 дней</option>
                <option value="45">45 дней</option>
                <option value="60">60 дней</option>
                <option value="90">90 дней</option>
                <option value="custom">Своё значение</option>
              </select>
            </label>
            {![0, 30, 45, 60, 90].includes(draft.paymentTermsDays ?? 30) ? (
              <label>
                <span>Своё значение, дней</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={draft.paymentTermsDays ?? 0}
                  onChange={(event) => update('paymentTermsDays', Number(event.target.value))}
                />
              </label>
            ) : null}
            <label className="span-2">
              <span>Общий комментарий</span>
              <textarea rows={3} value={draft.notes} onChange={(event) => update('notes', event.target.value)} />
            </label>
          </div>
          <button className="button secondary full-width" type="button" onClick={saveCard}>
            <Save size={17} />
            Сохранить карточку
          </button>
        </section>

        <section className="panel-section client-invoices">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Расчёты</span>
              <h3>Выставленные счета</h3>
            </div>
            <span>{invoices.length}</span>
          </div>
          {invoices.length ? (
            <div className="client-invoice-list">
              {invoices.slice(0, 8).map((invoice) => {
                const timing = invoiceTiming(invoice)
                return (
                  <article key={invoice.id} className={timing.tone}>
                    <div>
                      <ReceiptText size={16} />
                      <strong>№ {invoice.number}</strong>
                      <span>{new Intl.NumberFormat('ru-RU', {
                        style: 'currency',
                        currency: 'RUB',
                      }).format(invoice.amountKopecks / 100)}</span>
                    </div>
                    <small>{timing.label}</small>
                    <select
                      aria-label={`Статус счёта ${invoice.number}`}
                      value={invoice.status}
                      disabled={!onSaveInvoice}
                      onChange={(event) => {
                        const status = event.target.value as Invoice['status']
                        const today = todayInMoscow()
                        const shippedOn = status === 'shipped' || status === 'paid'
                          ? latestDateOnly(invoice.shippedOn, invoice.issuedOn, today)
                          : null
                        onSaveInvoice?.({
                          ...invoice,
                          status,
                          shippedOn,
                          paidOn: status === 'paid'
                            ? latestDateOnly(invoice.paidOn, shippedOn, today)
                            : null,
                          updatedAt: new Date().toISOString(),
                        })
                      }}
                    >
                      <option value="issued">Счёт выставлен</option>
                      <option value="shipped">Отгружено</option>
                      <option value="paid">Оплачено</option>
                      <option value="cancelled">Отменено</option>
                    </select>
                  </article>
                )
              })}
            </div>
          ) : <p className="panel-empty">У клиента пока нет счетов.</p>}
        </section>

        <section className="panel-section contact-box">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Быстрая фиксация</span>
              <h3>Результат контакта</h3>
            </div>
            <span className="attempts">Попыток: {draft.attempts}</span>
          </div>
          <label>
            <span>Чем закончился контакт</span>
            <select value={contactStatus} onChange={(event) => setContactStatus(event.target.value as ClientStatus)}>
              {CLIENT_STATUS_OPTIONS.map((status) => (
                <option value={status.value} key={status.value}>{status.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Что обсудили / о чём договорились</span>
            <textarea
              rows={4}
              placeholder="Например: попросил прислать предложение, вернуться в четверг…"
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
            />
          </label>
          <label>
            <span>Следующий контакт</span>
            <div className="input-with-icon">
              <CalendarClock size={17} />
              <input
                type="datetime-local"
                value={nextContact}
                onChange={(event) => setNextContact(event.target.value)}
              />
            </div>
          </label>
          <button className="button primary full-width" type="button" onClick={logContact}>
            Зафиксировать контакт
          </button>
        </section>

        {draft.interactions.length ? (
          <section className="panel-section">
            <div className="section-heading">
              <h3>История</h3>
              <span>{draft.interactions.length}</span>
            </div>
            <div className="timeline">
              {draft.interactions.slice(0, 8).map((interaction) => (
                <article key={interaction.id}>
                  <span className="timeline-dot" />
                  <div>
                    <time>{formatDate(interaction.createdAt)}</time>
                    <strong>
                      {CLIENT_STATUS_OPTIONS.find((status) => status.value === interaction.status)?.label}
                    </strong>
                    {interaction.outcome ? <p>{interaction.outcome}</p> : null}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <button className="danger-link" type="button" onClick={() => onDelete(draft)}>
          <Trash2 size={16} />
          Удалить клиента
        </button>
      </div>
    </aside>
  )
}
